import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { useFetchClubEventsQuery, useFetchClubMembersQuery, type EventOccurrence } from "@/api/clubsApi";
import { isOwnerOrCohost } from "@tinyclub/shared/models";
import { EventCard, type EventCardHost } from "@/components/events/EventCard";
import { Skeleton } from "@/components/ui/Skeleton";
import { Icon } from "@/components/ui/Icon";

interface UpcomingEventsSectionProps {
  clubId: string;
  userId?: string | undefined;
  onEventClick?: (eventId: string) => void;
  /** Callback to determine if user can edit this event */
  canEditEvent?: (event: EventOccurrence) => boolean;
  /** Callback to determine if user can delete this event */
  canDeleteEvent?: (event: EventOccurrence) => boolean;
  onEditEvent?: (event: EventOccurrence) => void;
  onDeleteEvent?: (event: EventOccurrence) => void;
}

/**
 * UpcomingEventsSection - Displays all ongoing and upcoming events for a club.
 * The API already filters to future events (occurrence_start >= now OR end_time >= now).
 */
export function UpcomingEventsSection({
  clubId,
  userId,
  onEventClick,
  canEditEvent,
  canDeleteEvent,
  onEditEvent,
  onDeleteEvent,
}: UpcomingEventsSectionProps) {
  const {
    data: events = [],
    isLoading,
    error,
  } = useFetchClubEventsQuery({ clubId, includeAttendees: true });

  const { data: members } = useFetchClubMembersQuery({
    clubId,
    includeUserProfiles: true,
  });

  // Build hosts map from club members (same pattern as EventsList)
  const hostsMap = useMemo(() => {
    if (!members || !events) return new Map<string, EventCardHost[]>();
    const map = new Map<string, EventCardHost[]>();

    for (const event of events) {
      const rootEvent = event.rootEvent;
      if (!rootEvent) continue;

      const hosts: EventCardHost[] = [];

      // Creator first
      const creatorMember = members.find((m) => m.user_id === rootEvent.creator_id);
      if (creatorMember?.user_profile) {
        const p = creatorMember.user_profile;
        hosts.push({
          userId: creatorMember.user_id,
          firstName: p.first_name || "Unknown",
          lastName: p.last_name || "",
          avatarUrl: p.avatar_small || p.avatar_medium || undefined,
        });
      }

      // Then owner/cohost members (not already added as creator)
      for (const m of members) {
        if (m.user_id === rootEvent.creator_id) continue;
        if (isOwnerOrCohost(m.role)) {
          const p = m.user_profile;
          if (p) {
            hosts.push({
              userId: m.user_id,
              firstName: p.first_name || "Unknown",
              lastName: p.last_name || "",
              avatarUrl: p.avatar_small || p.avatar_medium || undefined,
            });
          }
        }
      }

      map.set(event.id, hosts);
    }

    return map;
  }, [members, events]);

  if (isLoading) {
    return <UpcomingEventsSkeleton />;
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-text-secondary">Failed to load events.</p>
        <p className="text-text-tertiary text-sm mt-1">Please try again later.</p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-surface-2 flex items-center justify-center">
          <Icon name="calendar" size={32} className="text-text-tertiary" />
        </div>
        <p className="text-text-secondary font-medium">No upcoming events</p>
        <p className="text-text-tertiary text-sm mt-1">
          Check back later for new events.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-3">
      {events.map((event) => {
        const canEdit = canEditEvent?.(event) ?? false;
        const canDelete = canDeleteEvent?.(event) ?? false;
        const overflowProps = {
          canEdit,
          canDelete,
          onEdit: canEdit ? () => onEditEvent?.(event) : undefined,
          onDelete: canDelete ? () => onDeleteEvent?.(event) : undefined,
        };

        return onEventClick ? (
          <div
            key={event.id}
            onClick={() => onEventClick(event.id)}
            className="block w-full text-left cursor-pointer"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onEventClick(event.id);
              }
            }}
          >
            <EventCard event={event} currentUserId={userId} hosts={hostsMap.get(event.id)} showClubInfo={false} {...overflowProps} />
          </div>
        ) : (
          <Link
            key={event.id}
            to="/events/$identifier"
            params={{ identifier: event.id }}
            className="block"
          >
            <EventCard event={event} currentUserId={userId} hosts={hostsMap.get(event.id)} showClubInfo={false} {...overflowProps} />
          </Link>
        );
      })}
    </div>
  );
}

function UpcomingEventsSkeleton() {
  return (
    <div className="p-4 space-y-3">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="bg-surface-1 rounded-lg border border-border-tertiary p-4"
        >
          <div className="flex gap-4">
            <Skeleton className="w-20 h-20 rounded-md" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
